/** 房间配置与默认值。见 docs/ARCHITECTURE.md §3.4。 */
import type { Chips } from '../ids.js'
import type { DeckVariant } from '../poker/types.js'
import { ROOM_LIFECYCLE, SEAT } from './constants.js'

export type ShowdownPolicy = 'revealAll' | 'contendersOnly'
export type NewPlayerPolicy = 'waitForBigBlind' | 'dealInImmediately'
export type MissedBlindPolicy = 'waitForBigBlind' | 'postOnReturn'
export type AnteMode = 'none' | 'each' | 'bigBlindAnte'

export interface AssistPolicy {
  /** 'off' = 客户端连自己的胜率面板都不显示；'ownTurnOnly' = 仅本人行动回合内计算 */
  readonly equity: 'off' | 'ownTurnOnly' | 'always'
  readonly bestHand: boolean
}

export interface RoomConfig {
  readonly name: string
  readonly maxSeats: number // SEAT_LIMITS.MIN..MAX（2..8）
  readonly smallBlind: Chips // >=1
  readonly bigBlind: Chips // >=2 且 >= smallBlind
  readonly anteMode: AnteMode
  readonly ante: Chips // anteMode!=='none' 时 >=1
  readonly startingStack: Chips
  readonly allowRebuy: boolean
  readonly rebuyAmount: Chips

  readonly actionTimeoutMs: number
  readonly timeBankMs: number
  readonly interHandDelayMs: number
  readonly autoStart: boolean
  readonly minPlayersToStart: number // >=2

  readonly showdownPolicy: ShowdownPolicy
  readonly newPlayerPolicy: NewPlayerPolicy
  readonly missedBlindPolicy: MissedBlindPolicy

  readonly assist: AssistPolicy
  readonly spectatorsAllowed: boolean
  readonly chatEnabled: boolean
  readonly singleConnectionPerAccount: boolean
  readonly hostCanAdjustStacks: boolean
  readonly botsAllowed: boolean
  readonly maxBots: number
  /** 默认 false：0 真人即挂起 */
  readonly allowBotOnly: boolean
  /** 玩法：标准德州或短牌（6+）。建房时定，开局后不能改 */
  readonly deckVariant: DeckVariant
}

/**
 * 每桌人数范围。maxSeats 是房间配置项（建房时可选），不是写死的常量；
 * ROOM_DEFAULTS.maxSeats 只是默认值。
 * 上限取 8 而非 9：9 人在 375px 宽屏是几何极限，座位块与昵称都要压到不可读。
 */
export const SEAT_LIMITS = { MIN: 2, MAX: 8 } as const

export const ROOM_DEFAULTS: RoomConfig = {
  name: '',
  // 待拍板问题 4 的默认裁决：6 人桌。9 人在 375px 宽屏是几何极限。
  maxSeats: 6,
  smallBlind: 10,
  bigBlind: 20,
  anteMode: 'none',
  ante: 0,
  startingStack: 2000,
  allowRebuy: true,
  rebuyAmount: 2000,
  actionTimeoutMs: SEAT.ACTION_TIMEOUT_MS,
  timeBankMs: SEAT.TIMEBANK_MS,
  interHandDelayMs: ROOM_LIFECYCLE.INTER_HAND_DELAY_MS,
  autoStart: true,
  minPlayersToStart: 2,
  showdownPolicy: 'revealAll',
  newPlayerPolicy: 'waitForBigBlind',
  missedBlindPolicy: 'waitForBigBlind',
  assist: { equity: 'always', bestHand: true },
  spectatorsAllowed: false,
  chatEnabled: true,
  singleConnectionPerAccount: false,
  hostCanAdjustStacks: false,
  botsAllowed: true,
  maxBots: 5,
  allowBotOnly: false,
  deckVariant: 'standard',
}

/**
 * 生效时机。lobbyOnly 字段在 running/paused 下修改返回 E_CONFIG_LOCKED；
 * createOnly 字段（玩法）只能在建房时定，之后任何阶段都不能改 ——
 * 同一个房间的战绩、回放、机器人统计都默认整局是同一种牌。
 */
export const CONFIG_MUTABILITY: Record<keyof RoomConfig, 'anytime' | 'nextHand' | 'lobbyOnly' | 'createOnly'> = {
  name: 'anytime',
  chatEnabled: 'anytime',
  assist: 'anytime',
  spectatorsAllowed: 'anytime',
  actionTimeoutMs: 'nextHand',
  timeBankMs: 'nextHand',
  interHandDelayMs: 'nextHand',
  showdownPolicy: 'nextHand',
  smallBlind: 'nextHand',
  bigBlind: 'nextHand',
  anteMode: 'nextHand',
  ante: 'nextHand',
  allowRebuy: 'nextHand',
  rebuyAmount: 'nextHand',
  botsAllowed: 'nextHand',
  maxBots: 'nextHand',
  autoStart: 'nextHand',
  minPlayersToStart: 'nextHand',
  newPlayerPolicy: 'nextHand',
  missedBlindPolicy: 'nextHand',
  singleConnectionPerAccount: 'nextHand',
  hostCanAdjustStacks: 'nextHand',
  allowBotOnly: 'nextHand',
  maxSeats: 'lobbyOnly',
  startingStack: 'lobbyOnly',
  deckVariant: 'createOnly',
}
